import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { ROUTE_PATHS } from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { Layout } from "@/components/Layout";
import { LayoutEn } from "@/components/LayoutEn";
import { LayoutEs } from "@/components/LayoutEs";
import Home from "@/pages/Home";
import Company from "@/pages/Company";
import Production from "@/pages/Production";
import Products from "@/pages/Products";
import Sustainability from "@/pages/Sustainability";
import Contact from "@/pages/Contact";
import ProfileGuide from "@/pages/ProfileGuide";

// English Pages
import HomeEn from "@/pages/HomeEn";
import CompanyEn from "@/pages/CompanyEn";
import ProductionEn from "@/pages/ProductionEn";
import ProductsEn from "@/pages/ProductsEn";
import SustainabilityEn from "@/pages/SustainabilityEn";
import ContactEn from "@/pages/ContactEn";
import ProfileGuideEn from "@/pages/ProfileGuideEn";

// Spanish Pages
import HomeEs from "@/pages/HomeEs";
import CompanyEs from "@/pages/CompanyEs";
import ProductsEs from "@/pages/ProductsEs";
import ContactEs from "@/pages/ContactEs";
import SustainabilityEs from "@/pages/SustainabilityEs";

// Product Detail Pages
import ArchitecturalSystems from "@/pages/ArchitecturalSystems";
import ArchitecturalSystemsEn from "@/pages/ArchitecturalSystemsEn";
import IndustrialProfiles from "@/pages/IndustrialProfiles";
import IndustrialProfilesEn from "@/pages/IndustrialProfilesEn";
import FacadeSystems from "@/pages/FacadeSystems";
import FacadeSystemsEn from "@/pages/FacadeSystemsEn";
import AluminumBillet from "@/pages/AluminumBillet";
import AluminumBilletEn from "@/pages/AluminumBilletEn";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Root landing page — renders content for bots/social previews and redirects users to /en
const RootLanding = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    navigate("/en", { replace: true });
  }, [navigate]);

  // Render minimal content so social media crawlers / bots can read meta tags
  return (
    <div style={{ display: "none" }} aria-hidden="true">
      <h1>Mimalum – Aluminum Extrusion Manufacturer</h1>
      <p>Founded in 2020, Mimalum is a leading aluminum extrusion manufacturer based in Turkey, producing architectural systems, industrial profiles, facade systems and aluminum billets for global markets.</p>
      <img src="/images/LOGO SON TASARIM.jpg" alt="Mimalum Logo" />
    </div>
  );
};

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = (): null => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/**
 * Root application component with React Router setup.
 * Configures routes for the industrial aluminum factory website.
 * © 2026 Sistemal Industrial Aluminum.
 */
const App = () => {
  // Hash navigation handler
  React.useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            const headerOffset = 160; // Header + navigation height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }, 100); // Small delay to ensure page is loaded
      }
    };

    // Handle hash on initial load
    handleHashNavigation();

    // Handle hash changes
    window.addEventListener('hashchange', handleHashNavigation);
    return () => window.removeEventListener('hashchange', handleHashNavigation);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <MotionConfig reducedMotion="user">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Root route — serves content for bots/SEO, redirects real users to /en */}
              <Route path="/" element={<RootLanding />} />
              
              {/* Turkish Routes */}
              <Route path="/tr/*" element={
                <Layout>
                  <Routes>
                    <Route path="" element={<Home />} />
                    <Route path="kurumsal" element={<Company />} />
                    <Route path="uretim" element={<Production />} />
                    <Route path="uretim/dokumhane" element={<Production />} />
                    <Route path="uretim/ekstruzyon" element={<Production />} />
                    <Route path="uretim/yuzey-islem" element={<Production />} />
                    <Route path="uretim/mekanik-islem" element={<Production />} />
                    <Route path="uretim/kalip-imalati" element={<Production />} />
                    <Route path="urunler" element={<Products />} />
                    <Route path="urunler/mimari-sistemler" element={<Products />} />
                    <Route path="urunler/endustriyel-profiller" element={<Products />} />
                    <Route path="mimari-sistemler" element={<ArchitecturalSystems />} />
                    <Route path="endustriyel-profiller" element={<IndustrialProfiles />} />
                    <Route path="cephe-dograma-sistemleri" element={<FacadeSystems />} />
                    <Route path="aluminyum-billet" element={<AluminumBillet />} />
                    <Route path="aluminyum-kulce" element={<AluminumBillet />} />
                    <Route path="surdurulebilirlik" element={<Sustainability />} />
                    <Route path="profil-rehberi" element={<ProfileGuide />} />
                    <Route path="iletisim" element={<Contact />} />
                    <Route path="indirilebilirler" element={<Products />} />
                    <Route path="*" element={<Home />} />
                  </Routes>
                </Layout>
              } />
              
              {/* English Routes */}
              <Route path="/en/*" element={
                <LayoutEn>
                  <Routes>
                    <Route path="" element={<HomeEn />} />
                    <Route path="about" element={<CompanyEn />} />
                    <Route path="production" element={<ProductionEn />} />
                    <Route path="production/foundry" element={<ProductionEn />} />
                    <Route path="production/extrusion" element={<ProductionEn />} />
                    <Route path="production/coating" element={<ProductionEn />} />
                    <Route path="production/mechanical" element={<ProductionEn />} />
                    <Route path="production/mold" element={<ProductionEn />} />
                    <Route path="products" element={<ProductsEn />} />
                    <Route path="products/architectural" element={<ProductsEn />} />
                    <Route path="products/industrial" element={<ProductsEn />} />
                    <Route path="architectural-systems" element={<ArchitecturalSystemsEn />} />
                    <Route path="industrial-profiles" element={<IndustrialProfilesEn />} />
                    <Route path="facade-systems" element={<FacadeSystemsEn />} />
                    <Route path="aluminum-billet" element={<AluminumBilletEn />} />
                    <Route path="aluminum-ingot" element={<AluminumBilletEn />} />
                    <Route path="sustainability" element={<SustainabilityEn />} />
                    <Route path="profile-guide" element={<ProfileGuideEn />} />
                    <Route path="contact" element={<ContactEn />} />
                    <Route path="downloads" element={<ProductsEn />} />
                    <Route path="*" element={<HomeEn />} />
                  </Routes>
                </LayoutEn>
              } />
              
              {/* Spanish Routes */}
              <Route path="/es/*" element={
                <LayoutEs>
                  <Routes>
                    <Route path="" element={<HomeEs />} />
                    <Route path="empresa" element={<CompanyEs />} />
                    <Route path="produccion" element={<Production />} />
                    <Route path="produccion/fundicion" element={<Production />} />
                    <Route path="produccion/extrusion" element={<Production />} />
                    <Route path="produccion/recubrimiento" element={<Production />} />
                    <Route path="produccion/mecanico" element={<Production />} />
                    <Route path="produccion/molde" element={<Production />} />
                    <Route path="productos" element={<ProductsEs />} />
                    <Route path="productos/arquitectonicos" element={<ProductsEs />} />
                    <Route path="productos/industriales" element={<ProductsEs />} />
                    <Route path="sistemas-arquitectonicos" element={<ArchitecturalSystems />} />
                    <Route path="perfiles-industriales" element={<IndustrialProfiles />} />
                    <Route path="sistemas-fachada" element={<FacadeSystems />} />
                    <Route path="sostenibilidad" element={<SustainabilityEs />} />
                    <Route path="guia-perfiles" element={<ProfileGuide />} />
                    <Route path="contacto" element={<ContactEs />} />
                    <Route path="descargas" element={<ProductsEs />} />
                    <Route path="*" element={<HomeEs />} />
                  </Routes>
                </LayoutEs>
              } />
            </Routes>
          </BrowserRouter>
        </MotionConfig>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;